/**
 * Markdown → React renderer for the dashboard audit viewer.
 *
 * Uses react-markdown + remark-gfm so we get XSS-safe rendering, tables,
 * strikethrough, task lists, autolinks. Components are mapped to the
 * dashboard's honey/amber theme via CSS variables from src/index.css.
 *
 * Why a library: the audit reports come from an LLM. Even though we
 * trust our own pipeline, defence-in-depth (markdown library that
 * sanitises HTML by default) beats a hand-rolled regex parser.
 */

import type { ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

const components: Components = {
  h1: ({ children }) => (
    <h1 className="font-display italic text-3xl text-accent-gradient mt-8 mb-4">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="font-display italic text-2xl text-[var(--color-accent)] mt-7 mb-3">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="font-semibold text-base text-[var(--color-text)] mt-5 mb-2 tracking-tight">
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p className="text-sm text-[var(--color-text)] leading-relaxed my-3">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc pl-6 space-y-1 my-3 text-sm text-[var(--color-text)]">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-6 space-y-1 my-3 text-sm text-[var(--color-text)]">{children}</ol>
  ),
  li: ({ children }) => <li className="text-sm text-[var(--color-text)]">{children}</li>,
  strong: ({ children }) => (
    <strong className="font-semibold text-[var(--color-text)]">{children}</strong>
  ),
  em: ({ children }) => <em className="italic text-[var(--color-text-muted)]">{children}</em>,
  hr: () => <hr className="border-t border-[var(--color-border)] my-6" />,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-[var(--color-accent-bright)] underline decoration-[var(--color-accent)]/40 hover:decoration-[var(--color-accent)] transition-colors"
    >
      {children}
    </a>
  ),
  code: ({ className, children }) => {
    // Inline `code` vs ``` blocks: fenced code blocks get a language
    // class, inline doesn't. react-markdown passes className for both.
    const isInline = !/language-/.test(className ?? "");
    if (isInline) {
      return (
        <code className="font-mono text-[12px] bg-[var(--color-bg-card)] text-[var(--color-accent-bright)] px-1.5 py-0.5 rounded">
          {children}
        </code>
      );
    }
    return (
      <code className="font-mono text-[12px] text-[var(--color-text)] block">{children}</code>
    );
  },
  pre: ({ children }) => (
    <pre className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded p-4 my-4 overflow-x-auto font-mono text-[12px]">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[var(--color-accent)] pl-4 my-4 italic text-[var(--color-text-muted)]">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto">
      <table className="w-full border-collapse border border-[var(--color-border)]">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-[var(--color-bg-card)]">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="border border-[var(--color-border)] px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-[var(--color-text)]">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-text)]">
      {children}
    </td>
  ),
};

export function renderMarkdown(md: string): ReactNode {
  return <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{md}</ReactMarkdown>;
}
